import React from 'react';
import { KbSection, Callout, StepList, VisibleTo } from '../../components/knowledge/primitives.jsx';
import { hasAdminTab } from '../../auth/roles.js';
import RuleBuilderDemo from '../../components/knowledge/demos/RuleBuilderDemo.jsx';
import ImpersonationDemo from '../../components/knowledge/demos/ImpersonationDemo.jsx';

// "Administration" articles. Most require canManageAdminConfig (SuperAdmin);
// the panel tour and tags-rules also show for roles with a scoped tab subset
// (Supervisor: Tags & Rules, Cost, Changelog — see ADMIN_TABS_BY_ROLE).

function AdminPanelTourBody({ user }) {
  return (
    <>
      <KbSection title="Getting there">
        <p>
          The Admin Panel lives in the <strong>user menu</strong> — click your name at the
          bottom of the sidebar and choose <strong>Admin Panel</strong>. What it offers
          depends on your role: Super Admins get every tab; Supervisors get a focused set —
          Tags &amp; Rules, Cost and the Changelog. The tabs below are the ones your role
          can open.
        </p>
      </KbSection>

      <KbSection title="The tabs" delay={1}>
        <StepList steps={[
          ...(hasAdminTab(user, 'Case Config') ? [
            <><strong>Case Config</strong> — event types, severity codes, status values, treatment and requirement types, the legal threshold, and case-list defaults (page size, sort, filter groups).</>,
          ] : []),
          ...(hasAdminTab(user, 'Incident Model') ? [
            <><strong>Incident Model</strong> — the incident-category options offered on the form, per treatment and severity code.</>,
          ] : []),
          ...(hasAdminTab(user, 'Tags & Rules') ? [
            <><strong>Tags &amp; Rules</strong> — the tag vocabulary and the auto-tagging rules that apply tags for you.</>,
          ] : []),
          ...(hasAdminTab(user, 'Notifications') ? [
            <><strong>Notifications</strong> — the built-in notification flows and your custom flow rules.</>,
          ] : []),
          ...(hasAdminTab(user, 'Cost') ? [
            <><strong>Cost</strong> — cost categories and currencies for the case cost panel.</>,
          ] : []),
          ...(hasAdminTab(user, 'Users & Security') ? [
            <><strong>Users &amp; Security</strong> — who can sign in, their role, PIN resets, and the reset-to-defaults danger zone.</>,
          ] : []),
          ...(hasAdminTab(user, 'Developer') ? [
            <><strong>Developer</strong> — API reference: base URL, auth model, and the endpoint catalogue.</>,
          ] : []),
          ...(hasAdminTab(user, 'Changelog') ? [
            <><strong>Changelog</strong> — the release history, version by version.</>,
          ] : []),
        ]} />
      </KbSection>

      <VisibleTo user={user} when={(u) => !!u?.canManageAdminConfig}>
        <KbSection title="Draft, review, publish" delay={2}>
          <p>
            The core taxonomies — severity codes, status values, treatment types, requirement
            types and the incident model — are <strong>governed</strong>: your edits build up a
            local draft (a <strong>Draft</strong> badge appears) and nothing is persisted until
            you press <strong>Review &amp; publish</strong>. The confirm dialog shows a diff of
            exactly what will change — each line tagged <strong>ADD</strong>,
            <strong> REMOVE</strong> or <strong>EDIT</strong> — and you choose Publish, Discard
            draft, or Cancel to keep editing. Validation errors block publishing until fixed.
          </p>
          <p>
            Lighter settings — event-type labels, the legal threshold, cost categories,
            currencies and the case-list defaults — save directly with their own
            <strong> Save</strong> buttons, no draft step.
          </p>
        </KbSection>

        <KbSection title="Every change is on the record" delay={3}>
          <p>
            Each governed section has a <strong>History</strong> button that opens its change
            history: who published, when, and a summary of what changed. Each entry is cryptographically linked to the one
            before it, so history can't be quietly rewritten — <strong>Verify audit
            integrity</strong> checks every row and reports either “Chain intact” or the
            exact row where something was altered.
          </p>
          <Callout tone="info">
            Publishing takes effect for everyone on their next page load — there is no separate
            deploy step for configuration.
          </Callout>
        </KbSection>
      </VisibleTo>

      <VisibleTo user={user} when={(u) => !u?.canManageAdminConfig}>
        <KbSection title="Saving your changes" delay={2}>
          <p>
            Everything in your tabs saves directly: tag and rule edits apply as you confirm
            them, and the Cost tab's categories and currencies save with their own
            <strong> Save</strong> buttons. Every change is recorded with your name — the
            same audit trail Super Admins review.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function AdminVocabulariesBody() {
  return (
    <>
      <KbSection title="What lives where">
        <p>
          The <strong>Case Config</strong> tab holds the case vocabularies: event types
          (Treatment incident, Near miss, Clinic incident), severity codes (Code 1 to
          Code 5 in the default setup), status values, treatment types and requirement
          types. The <strong>Incident Model</strong> tab holds the cascade that decides
          which incident categories the form offers for a given treatment and severity code.
        </p>
        <p>
          Event types are special: their internal value is fixed because each type has its
          own form — you can edit the label, hint, order and whether the type is offered,
          but not add new ones from config.
        </p>
      </KbSection>

      <KbSection title="Editing a taxonomy" delay={1}>
        <StepList steps={[
          <>Click <strong>Edit</strong> on a row to change its label (the label drives the stored value), description, badge colour and which event types it is available for.</>,
          <>Drag the <strong>⠿</strong> handle to reorder — or focus it and use the arrow keys. The order is the order users see.</>,
          <><strong>+ Add</strong> creates a new row; <strong>Remove</strong> takes one out of the pick lists.</>,
          <>Press <strong>Review &amp; publish</strong> and confirm the diff to make it live.</>,
        ]} />
      </KbSection>

      <KbSection title="The incident-category cascade" delay={2}>
        <p>
          On the Incident Model tab each treatment is an accordion section. Inside, every
          severity code has its own ordered list of categories — what a reporter picking
          that treatment and code will see. Treatments can carry <strong>subtypes</strong>
          (for example Toxin and Filler under AWI), which add one more level: each subtype
          gets its own per-code lists. Adding a category named <strong>Other</strong> to a
          list enables free text on the form for that cell.
        </p>
        <p>
          Subtypes are managed inline — add, rename, or remove. Removing one asks whether to
          keep its category data (so re-adding the subtype restores it) or delete it.
          Publishing the incident model always publishes the treatment list with it, as one
          combined change, so the two can never drift apart.
        </p>
      </KbSection>

      <KbSection title="Overrides, defaults and retiring values" delay={3}>
        <p>
          What you publish overrides the app's built-in defaults, entry by entry — and
          built-ins only ever add: when a release ships a new built-in treatment or
          severity code, it appears alongside your published entries without a re-publish — which also means
          removing a built-in entry does not stick, it merges back on the next load.
        </p>
        <Callout tone="warn">
          Prefer retiring over deleting. Mark a status <strong>Inactive</strong> (hidden from
          filters), disable an event type, or archive a cost category — existing cases keep
          their stored values either way, but a removed entry leaves old data pointing at a
          value the pickers no longer explain.
        </Callout>
      </KbSection>
    </>
  );
}

function TagsRulesBody() {
  return (
    <>
      <KbSection title="User tags and system tags">
        <p>
          The <strong>Tags &amp; Rules</strong> tab shows the whole tag vocabulary with
          per-tag usage counts. <strong>Supervisors and Super Admins</strong> create ordinary
          tags here via <strong> + New tag</strong> (or from a case picker) — pick a label, a
          colour, and whether the tag shows in the case picker dropdown (hidden ones stay
          findable by typing). <strong>Admins and above</strong> assign existing tags on cases;
          they cannot mint new vocabulary entries. Tags with a lock
          icon are <strong>system tags</strong>: they are managed by a rule, users cannot add
          or remove them on a case, and they cannot be deleted here. Deleting an ordinary tag
          removes it from every case it is on.
        </p>
      </KbSection>

      <KbSection title="Building a rule" delay={1}>
        <p>
          Open any tag and choose <strong>Add rule</strong> (or <strong>Edit rule</strong>).
          A rule is a group of conditions matched with <strong>ALL (AND)</strong> or
          <strong> ANY (OR)</strong> — and groups nest, so “this AND (that OR the other)” is
          expressible. Conditions cover the case fields: treatment, treatment subtype,
          severity, status, event type, incident category, clinic, region and country, plus
          day-counts — days since incident, days since reported, days in current status and
          days until the legal limit. <strong>Count conditions</strong> match patterns across
          cases, like “same staff member concerned on at least 2 cases in the last 90 days”.
        </p>
        <RuleBuilderDemo />
      </KbSection>

      <KbSection title="The live preview" delay={2}>
        <p>
          As soon as every condition is complete, the builder shows how many existing cases
          the rule matches — a sanity check before you commit. <strong>Save rule &amp;
          lock</strong> makes it live and locks the tag as a system tag; clearing the rule
          later stops the auto-application, but the tag stays locked.
        </p>
      </KbSection>

      <KbSection title="When rules run" delay={3}>
        <StepList steps={[
          <>When a case is <strong>created</strong> — matching tags are applied immediately.</>,
          <>When a case is <strong>updated</strong> — tags are re-checked: newly matching ones added, no-longer-matching ones removed.</>,
          <>Every night — a sweep re-evaluates time-based rules, so a tag like “past the legal threshold” appears even if nobody touches the case.</>,
          <><strong>Re-apply rules</strong> on the tab header runs the same reconciliation across every case on demand — useful right after changing a rule.</>,
        ]} />
      </KbSection>
    </>
  );
}

function NotificationsRulesBody() {
  return (
    <>
      <KbSection title="System flows and custom rules">
        <p>
          The <strong>Notifications</strong> tab has two parts. <strong>System
          notifications</strong> are the built-in flows: “New case submitted” (you can toggle
          it and edit who receives it), “Sign-off completed → requester” (locked — the
          routing is intrinsic), and treatment-based routing that adds extra recipients to
          the new-case notification per treatment type. <strong>Custom notification
          rules</strong> are yours: trigger plus conditions, routed to recipients you choose.
        </p>
      </KbSection>

      <KbSection title="Trigger + conditions → recipients" delay={1}>
        <StepList steps={[
          <>Name the rule and pick its trigger: <strong>Case submitted</strong>, <strong>Status changed</strong>, <strong>Severity changed</strong> or <strong>Sign-off completed</strong>.</>,
          <>Optionally add conditions with the same builder used for auto-tag rules — case attributes only here (treatment, severity, region, clinic, status…), no time or count rules. No conditions means the rule fires on every matching event.</>,
          <>Add one or more recipients, then <strong>Save changes</strong> to publish. A live count shows how many current cases the conditions match, as a guide.</>,
        ]} />
      </KbSection>

      <KbSection title="Recipients and To/Cc/Bcc" delay={2}>
        <p>
          Each recipient is resolved when the notification is sent, and each carries its own
          <strong> To</strong>, <strong>Cc</strong> or <strong>Bcc</strong> channel. Five
          recipient types are available: a literal <strong>email address</strong>,
          <strong> Head of RO (by case region)</strong>, <strong>Clinic Manager (by case
          clinic)</strong>, everyone with a given <strong>job title</strong>, or a named
          <strong> role preset</strong> (for example “Injectables lead”). Recipients must be
          staff addresses — the server rejects anything outside the company domain — and each
          send is capped to a sane recipient count as a backstop.
        </p>
      </KbSection>

      <KbSection title="What the message contains" delay={3}>
        <Callout tone="warn">
          Notifications never carry client or clinical detail — only the case reference, the
          clinic and a link into the tracker. This is enforced at the template level, not
          left to rule authors, so no rule you write here can leak case content into email.
        </Callout>
      </KbSection>
    </>
  );
}

function UsersSecurityBody() {
  return (
    <>
      <KbSection title="Who can get in">
        <p>
          The <strong>Users &amp; Security</strong> tab starts with <strong>User access &amp;
          PIN</strong> — everyone who holds an app role, with their address, role and last
          PIN unlock. Roles are <strong>Entra app roles</strong>: this tab manages the
          assignments for you, but Entra remains the source of truth, and a change takes
          effect on the person's next sign-in.
        </p>
      </KbSection>

      <KbSection title="Managing access" delay={1}>
        <StepList steps={[
          <><strong>+ Add user</strong> — enter an email and pick a role; this is what grants access to the app.</>,
          <>Change a role with the dropdown on the person's row — applied at their next sign-in.</>,
          <><strong>Remove</strong> revokes their access at next sign-in.</>,
          <><strong>Reset PIN</strong> clears a user's 6-digit session PIN so they set a new one on next sign-in — the fix for a forgotten PIN or a lockout.</>,
        ]} />
      </KbSection>

      <KbSection title="Role definitions and the reference list" delay={2}>
        <p>
          <strong>Role Definitions</strong> holds the display label and description for each
          role — cosmetic only. The internal role value is fixed; changing what a role
          actually is requires an Entra app-role update. Below it, <strong>SuperAdmin
          Emails</strong> is clearly marked reference-only: the live gate is Entra, and
          editing that list grants or revokes nothing.
        </p>
      </KbSection>

      <KbSection title="The danger zone" delay={3}>
        <p>
          At the bottom sits <strong>Reset to Defaults</strong>: it overwrites every
          configuration key — vocabularies, rules, notifications, preferences — with the
          built-in defaults, behind an explicit confirmation.
        </p>
        <Callout tone="danger">
          Reset to Defaults cannot be undone. Your published taxonomies and rules are
          replaced wholesale — treat it as a last resort, not a cleanup tool.
        </Callout>
      </KbSection>
    </>
  );
}

function AssumeIdentityBody() {
  return (
    <>
      <KbSection title="See the app as someone else">
        <p>
          <strong>Assume identity</strong> is the troubleshooting tool for “why can't this
          user see…” questions: you view the app exactly as another user — their role, their
          clinic or region scope, their sidebar — without touching their account or asking
          them to screen-share.
        </p>
        <ImpersonationDemo />
      </KbSection>

      <KbSection title="Starting and stopping" delay={1}>
        <StepList steps={[
          <>Open the user menu and choose <strong>Assume identity…</strong></>,
          <>Enter the user's email — it must be an existing user, and their actual role plus clinic/region scope are resolved from that address, so you reproduce their exact view.</>,
          <>Confirm — the app fully reloads as that user, and every page fetches with their scope.</>,
          <>A purple banner stays pinned on every page with an <strong>Exit</strong> button; the user menu also offers <strong>Switch identity…</strong> and <strong>Exit impersonation</strong>.</>,
        ]} />
        <p>
          The assumed view survives a page refresh and ends when you exit or close the tab.
        </p>
      </KbSection>

      <KbSection title="Everything visible, nothing saved" delay={2}>
        <p>
          The assumed view is fully interactive — the comment composer, the New Submission
          wizard, edit controls and menus all appear exactly as they do for that user, so you
          see precisely what they see and can open. Nothing can actually be saved or posted:
          finishing an action (posting a comment, submitting a case) shows a notice instead,
          and the server rejects any change attempted while assuming. Only lesser roles can be
          assumed — never Super Admin.
        </p>
        <Callout tone="info">
          You can never be locked out: your real Supervisor or Super Admin identity is preserved underneath,
          and Exit works whatever role you assumed — even ReadOnly.
        </Callout>
      </KbSection>

      <KbSection title="Assuming a clinic mailbox" delay={3}>
        <p>
          A clinic signs in as one shared mailbox that several front-of-house people use, and
          its cases belong to whoever reported them — so the mailbox on its own has nothing to
          show. Assuming one puts a short list of that clinic's registered staff in front of
          you: pick a name and you get that person's view. You are never asked for their PIN
          (you could not know it, and nothing can be saved while assuming anyway).
        </p>
        <p>
          The picker covers the whole screen, so it carries its own <strong>Exit assumed
          identity</strong> button; once you have picked someone the usual purple banner
          returns, naming the person and offering <strong>Change person</strong> beside Exit.
        </p>
      </KbSection>

      <KbSection title="Answering “what can this user see?”" delay={4}>
        <p>
          Everything that adapts to the signed-in user adapts to the assumed one — including
          this Knowledge page, which filters its articles to the assumed tier. Assuming a
          Clinic Manager and opening Knowledge shows you precisely the guide that user reads,
          which is often the fastest answer to an access question.
        </p>
      </KbSection>
    </>
  );
}

export const ADMINISTRATION_ARTICLES = [
  {
    id: 'admin-panel-tour',
    title: 'Around the Admin Panel',
    category: 'administration',
    summary: 'The tabs, the draft → diff → publish pattern, and the audit trail behind every change.',
    keywords: ['admin', 'admin panel', 'tabs', 'publish', 'draft', 'diff', 'audit', 'config', 'history'],
    visibleTo: (u) => !!u?.adminTabs,
    Body: AdminPanelTourBody,
  },
  {
    id: 'admin-vocabularies',
    title: 'Case vocabularies & the incident model',
    category: 'administration',
    summary: 'Edit event types, severity codes, statuses and treatments, and the category cascade behind the form.',
    keywords: ['severity', 'status', 'treatment', 'event type', 'incident category', 'taxonomy', 'vocabulary', 'subtype', 'archive'],
    visibleTo: (u) => !!u?.canManageAdminConfig,
    Body: AdminVocabulariesBody,
  },
  {
    id: 'tags-rules',
    title: 'Auto-tagging rules',
    category: 'administration',
    summary: 'Build AND/OR rules that tag matching cases automatically, with a live match preview.',
    keywords: ['tags', 'auto-tag', 'rules', 'rule builder', 'system tag', 'preview', 'backfill', 'conditions'],
    visibleTo: (u) => hasAdminTab(u, 'Tags & Rules'),
    Body: TagsRulesBody,
  },
  {
    id: 'notifications-rules',
    title: 'Notification flow rules',
    category: 'administration',
    summary: 'Route case events to the right people: trigger + conditions → recipients, To/Cc/Bcc.',
    keywords: ['notifications', 'email', 'flow rules', 'recipients', 'trigger', 'cc', 'bcc', 'job title', 'role preset'],
    visibleTo: (u) => !!u?.canManageAdminConfig,
    Body: NotificationsRulesBody,
  },
  {
    id: 'users-security',
    title: 'Users, roles & security',
    category: 'administration',
    summary: 'Grant and change app access, reset PINs, and know what the danger zone really does.',
    keywords: ['users', 'roles', 'access', 'entra', 'pin', 'reset', 'security', 'danger zone', 'app role'],
    visibleTo: (u) => !!u?.canManageAdminConfig,
    Body: UsersSecurityBody,
  },
  {
    id: 'assume-identity',
    title: 'Assume identity',
    category: 'administration',
    summary: 'View the app exactly as another user — fully interactive, nothing can be saved, always-available Exit.',
    keywords: ['impersonation', 'assume identity', 'troubleshooting', 'read-only', 'view as', 'banner', 'switch identity', 'clinic mailbox', 'front of house'],
    visibleTo: (u) => !!u?.canImpersonate,
    Body: AssumeIdentityBody,
  },
];
